import {
  PROGRAM_INSTALLMENT_VALUE,
  PROGRAM_INSTALLMENTS,
  PROGRAM_MONTHS,
  PROGRAM_PIX_DISCOUNT_PERCENT,
  PROGRAM_PIX_PRICE,
  PROGRAM_PRICE,
  TIRZEPATIDA_NAME,
  TIRZEPATIDA_URL,
  formatBrl
} from '../constants'
import { cn } from '@cannect/lib/utils'
import {
  Activity,
  Apple,
  CalendarCheck,
  Check,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CreditCard,
  Clock,
  Heart,
  HeartPulse,
  Info,
  KeyRound,
  Loader2,
  Lock,
  MailCheck,
  MessageCircle,
  MessageCircleHeart,
  Package,
  Pill,
  Star,
  Stethoscope,
  TrendingUp
} from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { FaPix } from 'react-icons/fa6'
import {
  CHOICE_OPTIONS,
  CONSULTA_AVULSA_PATH,
  CONSULTA_CARD_PRICE,
  CONSULTA_PIX_PRICE,
  DECLINE_HELP_TEXT,
  DECLINE_OPTIONS,
  IMC_BANDS,
  SUPPORT_WHATSAPP_URL,
  formatImc,
  imcCategory
} from './copy'
import { TrailHeroBanner } from './illustrations'
// PROJEÇÃO DE PESO OCULTA (a pedido, até o time definir as regras do cálculo). Para reativar, descomente este import,
// a linha `const projected` do ImcResultScreen e os dois blocos "PROJEÇÃO DE PESO" abaixo.
// import { WeightProjectionChart, projectedWeight } from './illustrations'
import type { TProgramPaymentSummary } from './programCart'
import { TermsModal } from './TermsModal'
import { TrailCta, TrailGhost } from './TrailShell'
import { CPF_ERROR, EMAIL_ERROR, PHONE_ERROR, isValidCpf, isValidEmail, isValidPhone } from './validation'

const titleClass = 'm-0 text-[22px] font-bold leading-[1.25] text-[#1A1A1A] md:text-[30px] md:leading-[1.2]'
const bodyClass = 'm-0 text-sm leading-[1.5] text-[#433F3E] md:text-base'
const cardClass = 'rounded-2xl border border-solid border-[#ECECEC] bg-white'

const TirzepatidaLink = ({ children = TIRZEPATIDA_NAME }: { children?: ReactNode }) => (
  <a
    href={TIRZEPATIDA_URL}
    target="_blank"
    rel="noreferrer"
    className="font-semibold text-inherit underline underline-offset-2">
    {children}
  </a>
)

export const TrailHeading = ({
  eyebrow,
  title,
  sub,
  center
}: {
  eyebrow?: string
  title: string
  sub?: string | null
  center?: boolean
}) => (
  <div className={cn('flex flex-col', center && 'items-center text-center')}>
    {eyebrow ? (
      <p className="m-0 mb-1.5 text-xs font-semibold text-marketplace-800 md:mb-2 md:text-sm">{eyebrow}</p>
    ) : null}
    <h1 className={titleClass}>{title}</h1>
    {sub ? <p className={cn(bodyClass, 'mt-2')}>{sub}</p> : null}
  </div>
)

export const TrailPill = ({ icon, children }: { icon?: ReactNode; children: ReactNode }) => (
  <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-marketplace-200 px-3 py-1.5 text-xs font-medium text-marketplace-900">
    {icon}
    {children}
  </span>
)

const inputClass =
  'h-12 w-full rounded-xl border border-solid border-[#DADADA] bg-white px-4 text-base text-[#1A1A1A] outline-none placeholder:text-[#9A9A9A] focus:border-marketplace-800 md:h-14'

export const TrailField = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  inputMode,
  suffix,
  hint,
  error,
  readOnly
}: {
  label: string
  value: string
  onChange?: (value: string) => void
  type?: string
  placeholder?: string
  inputMode?: 'numeric' | 'decimal' | 'tel' | 'email' | 'text'
  suffix?: string
  hint?: string
  // Mensagem de campo inválido; tem prioridade sobre a dica.
  error?: string
  readOnly?: boolean
}) => {
  const id = useId()

  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-sm font-semibold text-[#1A1A1A]">{label}</span>
      <span className="relative block">
        <input
          id={id}
          className={cn(
            inputClass,
            (suffix || readOnly) && 'pr-12',
            readOnly && 'cursor-default bg-[#F7F7F7] text-[#433F3E]',
            error && 'border-[#DC4E5A]'
          )}
          aria-readonly={readOnly || undefined}
          aria-invalid={error ? true : undefined}
          type={type}
          value={value}
          placeholder={placeholder}
          inputMode={inputMode}
          readOnly={readOnly}
          onChange={(event) => onChange?.(event.target.value)}
        />
        {suffix ? (
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#6B6560]">
            {suffix}
          </span>
        ) : null}
        {readOnly && !suffix ? (
          <Lock
            aria-hidden
            className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-[#9A9A9A]"
          />
        ) : null}
      </span>
      {error ? (
        <span role="alert" className="mt-1.5 block text-xs text-[#DC4E5A]">
          {error}
        </span>
      ) : null}
      {!error && hint ? <span className="mt-1.5 block text-xs text-[#6B6560]">{hint}</span> : null}
    </label>
  )
}

const optionCardClass = (active: boolean) =>
  cn(
    'flex w-full cursor-pointer items-center gap-3 rounded-xl border border-solid px-4 py-3.5 text-left text-sm leading-snug text-[#1A1A1A] md:gap-4 md:px-5 md:py-[18px] md:text-base',
    active ? 'border-marketplace-800 bg-marketplace-200' : 'border-[#E8E8E8] bg-white'
  )

const RadioMark = ({ active }: { active: boolean }) => (
  <span
    className={cn(
      'inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-solid',
      active ? 'border-marketplace-900' : 'border-[#C4C0BF]'
    )}>
    {active ? <span className="block size-2.5 rounded-full bg-marketplace-900" /> : null}
  </span>
)

const CheckMark = ({ active }: { active: boolean }) => (
  <span
    className={cn(
      'inline-flex size-5 shrink-0 items-center justify-center rounded-[5px] border-2 border-solid',
      active ? 'border-marketplace-900 bg-marketplace-900' : 'border-[#C4C0BF] bg-white'
    )}>
    {active ? <Check className="size-3.5 text-white" strokeWidth={3} /> : null}
  </span>
)

const RadioCard = ({ active, onSelect, children }: { active: boolean; onSelect: () => void; children: ReactNode }) => (
  <button type="button" onClick={onSelect} className={optionCardClass(active)}>
    <RadioMark active={active} />
    <span>{children}</span>
  </button>
)

const CheckCard = ({ active, onSelect, children }: { active: boolean; onSelect: () => void; children: ReactNode }) => (
  <button type="button" onClick={onSelect} className={optionCardClass(active)}>
    <CheckMark active={active} />
    <span>{children}</span>
  </button>
)

const IconTile = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span
    className={cn(
      'inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-marketplace-200 text-marketplace-900',
      className
    )}>
    {children}
  </span>
)

export const ChoiceScreen = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div className="flex flex-1 flex-col gap-5">
    <TrailHeading
      eyebrow="Para começar"
      title="Antes de começarmos, me conta em qual estágio você está da sua jornada de emagrecimento?"
    />
    <div className="flex flex-col gap-2.5">
      {CHOICE_OPTIONS.map((option) => (
        <RadioCard key={option.id} active={value === option.id} onSelect={() => onChange(option.id)}>
          {option.label}
        </RadioCard>
      ))}
    </div>
  </div>
)

export const StartContactScreen = ({
  name,
  phone,
  email,
  consent,
  onName,
  onPhone,
  onEmail,
  onConsent,
  error
}: {
  name: string
  phone: string
  email: string
  consent: boolean
  onName: (value: string) => void
  onPhone: (value: string) => void
  onEmail: (value: string) => void
  onConsent: (value: boolean) => void
  error?: string
}) => {
  const [termsOpen, setTermsOpen] = useState(false)

  return (
    <div className="flex flex-1 flex-col gap-5">
      <TrailHeading
        eyebrow="Seus dados"
        title="Vamos começar?"
        sub="Precisamos de algumas informações para iniciar sua jornada."
      />
      <TrailField label="Nome completo" value={name} onChange={onName} placeholder="Digite seu nome" />
      <TrailField
        label="WhatsApp"
        value={phone}
        onChange={onPhone}
        type="tel"
        placeholder="(11) 90000-0000"
        error={phone && !isValidPhone(phone) ? PHONE_ERROR : undefined}
      />
      <TrailField
        label="E-mail"
        value={email}
        onChange={onEmail}
        type="email"
        inputMode="email"
        placeholder="voce@email.com"
        error={email.includes('@') && !isValidEmail(email) ? EMAIL_ERROR : undefined}
      />
      <label
        htmlFor="trail-consent"
        className="flex cursor-pointer items-start gap-3 text-[13px] leading-[1.45] text-[#433F3E]">
        <input
          id="trail-consent"
          type="checkbox"
          checked={consent}
          onChange={(event) => onConsent(event.target.checked)}
          className="mt-0.5 size-5 shrink-0 accent-marketplace-900"
        />
        <span>
          Li e concordo com os{' '}
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-[13px] font-bold text-marketplace-900 underline"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setTermsOpen(true)
            }}>
            Termos e Condições de Uso
          </button>{' '}
          do Programa de Cuidado para Perda de Peso e autorizo o contato da equipe para informações relacionadas a ele.
        </span>
      </label>
      {error ? <p className="m-0 text-sm text-[#DC4E5A]">{error}</p> : null}
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  )
}

export const TextCollectScreen = ({
  eyebrow,
  title,
  helper,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  fieldError,
  error
}: {
  eyebrow?: string
  title: string
  helper?: string
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  hint?: string
  fieldError?: string
  error?: string
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <TrailHeading eyebrow={eyebrow} title={title} sub={helper} />
    <TrailField
      label={label}
      value={value}
      onChange={onChange}
      type={type}
      placeholder={placeholder}
      hint={hint}
      error={fieldError}
    />
    {error ? <p className="m-0 text-sm text-[#DC4E5A]">{error}</p> : null}
  </div>
)

export const MeasuresScreen = ({
  height,
  weight,
  maxWeight,
  meta,
  onChange
}: {
  height: string
  weight: string
  maxWeight: string
  meta: string
  onChange: (field: 'height' | 'weight' | 'maxWeight' | 'meta', value: string) => void
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <div className="flex flex-col gap-3">
      <TrailHeading
        eyebrow="Sobre você"
        title="Conte um pouco sobre você"
        sub="As próximas perguntas nos ajudarão a entender seu perfil e organizar informações para uma possível avaliação por nossa equipe de saúde."
      />
      <p className="m-0 inline-flex items-center gap-2 text-[13px] text-[#433F3E]">
        <Clock className="size-4 text-[#6B6560]" />
        Leva aproximadamente 3 minutos.
      </p>
    </div>
    <div className="flex flex-col gap-5 md:grid md:grid-cols-2 md:items-end md:gap-x-6 md:gap-y-5">
      <TrailField
        label="Qual é a sua altura?"
        value={height}
        onChange={(value) => onChange('height', value)}
        inputMode="numeric"
        placeholder="170"
        suffix="cm"
      />
      <TrailField
        label="Qual é o seu peso atual?"
        value={weight}
        onChange={(value) => onChange('weight', value)}
        inputMode="decimal"
        placeholder="80"
        suffix="kg"
      />
      <TrailField
        label="Qual foi o maior peso que você já teve na vida adulta?"
        value={maxWeight}
        onChange={(value) => onChange('maxWeight', value)}
        inputMode="decimal"
        placeholder="90"
        suffix="kg"
      />
      <TrailField
        label="Qual peso você gostaria de alcançar?"
        value={meta}
        onChange={(value) => onChange('meta', value)}
        inputMode="decimal"
        placeholder="70"
        suffix="kg"
      />
    </div>
    <div className="flex items-start gap-2.5 rounded-xl bg-[#F3F4F6] px-4 py-3">
      <Info className="mt-0.5 size-4 shrink-0 text-[#433F3E]" />
      <p className="m-0 text-xs leading-[1.45] text-[#433F3E]">
        <strong className="font-semibold">Informação</strong>
        <br />
        Este questionário não realiza diagnóstico, não prescreve medicamentos e não substitui uma avaliação médica.
      </p>
    </div>
  </div>
)

const EXPECTATIONS = [
  {
    title: 'Consulta médica por teleatendimento',
    text: 'O médico avalia o seu caso e ajusta o plano quando for preciso, sem você sair de casa.'
  },
  {
    title: 'Acompanhamento de enfermagem',
    text: 'A enfermagem acompanha você de perto e tira dúvidas ao longo de todo o programa.'
  },
  {
    title: 'Alimentação que cabe na sua rotina',
    text: 'Com a nutricionista, você monta um jeito de comer possível no dia a dia.'
  },
  {
    title: 'Mudanças que duram',
    text: 'O foco é criar hábitos que continuem depois do programa.'
  },
  {
    title: 'Medicamento só quando indicado',
    text: 'Se fizer sentido para o seu caso, o médico avalia e indica o tratamento.'
  }
] as const

const ExpectationList = () => (
  <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
    {EXPECTATIONS.map((item) => (
      <li key={item.text} className="flex gap-3">
        <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-marketplace-300 text-marketplace-900">
          <Check className="size-3.5" strokeWidth={3} />
        </span>
        <span>
          <p className="m-0 text-sm font-semibold leading-snug text-[#1A1A1A]">{item.title}</p>
          <p className="m-0 mt-0.5 text-[13px] leading-[1.45] text-[#433F3E]">{item.text}</p>
        </span>
      </li>
    ))}
  </ul>
)

const ImcOutOfRangeScreen = ({ imc }: { imc: number }) => (
  <div className="flex flex-1 flex-col gap-5 pb-2">
    <div className="flex flex-col items-center gap-3 pt-1 text-center">
      <span className="inline-flex size-16 items-center justify-center rounded-full bg-marketplace-300">
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-marketplace-800 text-white">
          <Info className="size-5" />
        </span>
      </span>
      <h1 className={titleClass}>Você não tem perfil para o programa</h1>
      <span className="inline-flex items-center gap-2 rounded-full border border-solid border-[#DADADA] bg-white px-3.5 py-1.5 text-xs text-[#433F3E]">
        Seu IMC <strong className="text-sm font-bold text-[#1A1A1A]">{formatImc(imc)} kg/m²</strong>
      </span>
      <p className={bodyClass}>
        Pelo seu IMC, o Programa de Cuidado para Perda de Peso não é indicado neste momento. Se quiser conhecer outras
        opções, você pode agendar uma consulta médica.
      </p>
    </div>
    <section className={cn(cardClass, 'flex flex-col gap-2 p-4')}>
      <h2 className="m-0 text-sm font-semibold text-[#1A1A1A]">Consulta médica</h2>
      <p className="m-0 text-[13px] text-[#433F3E]">
        Converse com um médico para avaliar o seu caso e conhecer outras opções de cuidado.
      </p>
      <p className="m-0 flex justify-between text-[13px] text-[#433F3E]">
        <span>No Pix</span>
        <strong className="text-[#1A1A1A]">{formatBrl(CONSULTA_PIX_PRICE)}</strong>
      </p>
      <p className="m-0 flex justify-between text-[13px] text-[#433F3E]">
        <span>No cartão de crédito</span>
        <strong className="text-[#1A1A1A]">{formatBrl(CONSULTA_CARD_PRICE)}</strong>
      </p>
    </section>
  </div>
)

export const ImcResultScreen = ({
  imc,
  weight,
  goal,
  eligible = true
}: {
  imc: number
  weight: number
  goal?: number
  eligible?: boolean
}) => {
  const category = imcCategory(imc)
  // const projected = projectedWeight(weight) // PROJEÇÃO DE PESO (reativar junto com os blocos abaixo)

  if (!eligible) return <ImcOutOfRangeScreen imc={imc} />

  return (
    // No desktop o resultado (IMC + classificação) ocupa a coluna principal; o que esperar do programa fica numa lateral estreita.
    <div className="flex flex-1 flex-col gap-4 pb-2 md:grid md:flex-none md:grid-cols-[minmax(0,1fr)_300px] md:items-start md:gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-10">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-3 pt-1 text-center">
          <span className="inline-flex size-16 items-center justify-center rounded-full bg-marketplace-300">
            <span className="inline-flex size-10 items-center justify-center rounded-full bg-marketplace-800 text-white">
              <TrendingUp className="size-5" />
            </span>
          </span>
          {eligible ? (
            <p className="m-0 text-xs font-semibold text-marketplace-800">Você tem perfil para o programa</p>
          ) : null}
          <h1 className={titleClass}>Vamos juntos rumo ao seu objetivo</h1>
          <p className={bodyClass}>
            Pelas suas respostas, o programa pode ser um bom caminho para você. A indicação do tratamento depende da
            avaliação do médico na consulta.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-solid border-[#DADADA] bg-white px-3.5 py-1.5 text-xs text-[#433F3E]">
              Seu IMC <strong className="text-sm font-bold text-[#1A1A1A]">{formatImc(imc)} kg/m²</strong>
            </span>
            {eligible ? (
              <span className="inline-flex rounded-full bg-marketplace-300 px-3.5 py-1.5 text-xs font-semibold text-marketplace-900">
                {category}
              </span>
            ) : null}
          </div>
        </div>

        {eligible ? (
          <section className={cn(cardClass, 'p-4')}>
            <h2 className="m-0 text-sm font-semibold text-[#1A1A1A]">Classificação do IMC</h2>
            <div className="mt-3 grid grid-cols-4 gap-1.5">
              {IMC_BANDS.map((band) => {
                const active = imc >= band.min && imc < band.max
                return (
                  <div key={band.label} className="flex flex-col gap-1.5">
                    <span className={cn('h-1.5 rounded-full', active ? 'bg-marketplace-800' : 'bg-marketplace-300')} />
                    <span
                      className={cn('min-h-[2.6em] text-[11px] leading-tight text-[#1A1A1A]', active && 'font-bold')}>
                      {band.label}
                    </span>
                    <span className="text-[11px] text-[#6B6560]">{band.range}</span>
                  </div>
                )
              })}
            </div>
            <p className="m-0 mt-3 text-[11px] leading-snug text-[#6B6560]">
              Faixas para adultos, segundo a OMS e o Ministério da Saúde. A classificação é uma triagem, não um
              diagnóstico.
            </p>
          </section>
        ) : null}
      </div>

      {eligible ? (
        <section className={cn(cardClass, 'p-4 md:p-6')}>
          {/* PROJEÇÃO DE PESO (oculta a pedido; descomentar para reativar: título, estimativa e gráfico)
                <h2 className="m-0 text-base font-semibold leading-snug text-[#1A1A1A]">
                  Pessoas com perfil parecido com o seu chegam, em média, a{' '}
                  <span className="rounded-md bg-marketplace-200 px-1.5 py-0.5">{projected} kg</span> em 6 meses
                </h2>
                <p className="m-0 mt-1.5 text-[13px] text-[#433F3E]">
                  Uma perda estimada de {weight - projected} kg com o acompanhamento do programa.
                </p>
                <div className="mt-3">
                  <WeightProjectionChart weight={weight} goal={goal} />
                </div>
                <hr className="my-4 border-0 border-t border-solid border-[#EEEEEE]" />
            */}
          <h3 className="m-0 mb-3 text-sm font-semibold text-[#1A1A1A]">O que esperar do programa</h3>
          <ExpectationList />
          {/* PROJEÇÃO DE PESO (oculta a pedido; descomentar para reativar: aviso de estimativa ilustrativa)
              <p className="m-0 mt-4 text-[11px] leading-snug text-[#6B6560]">
                Estimativa ilustrativa. Os resultados variam de pessoa para pessoa e dependem da avaliação médica.
              </p>
            */}
        </section>
      ) : (
        <section className={cn(cardClass, 'p-4')}>
          <h2 className="m-0 mb-3 text-sm font-semibold text-[#1A1A1A]">O que esperar do programa</h2>
          <ExpectationList />
          <p className="m-0 mt-4 text-[11px] leading-snug text-[#6B6560]">
            Os resultados variam de pessoa para pessoa e dependem da avaliação médica.
          </p>
        </section>
      )}
    </div>
  )
}

export const ExitScreen = ({
  title,
  body,
  helpHref,
  helpText = 'Nossa equipe pode explicar por que o programa tem essa regra.'
}: {
  title: string
  body: string
  helpHref?: string
  helpText?: string
}) => {
  const help = (
    <>
      <IconTile className="size-9 rounded-lg">
        <Info className="size-4" />
      </IconTile>
      <div>
        <p className="m-0 text-sm font-semibold text-[#1A1A1A]">Ficou com dúvidas?</p>
        <p className="m-0 mt-0.5 text-xs leading-snug text-[#433F3E]">{helpText}</p>
      </div>
    </>
  )
  const helpClass = 'flex items-start gap-3 rounded-2xl border border-solid border-[#ECECEC] bg-[#F7F8FA] p-4'

  return (
    <div className="flex flex-1 flex-col gap-5">
      <TrailHeroBanner />
      <div className="flex flex-col gap-2">
        <h1 className={titleClass}>{title}</h1>
        <p className={bodyClass}>{body}</p>
      </div>
      {helpHref ? (
        <a href={helpHref} target="_blank" rel="noreferrer" className={cn(helpClass, 'no-underline')}>
          {help}
        </a>
      ) : (
        <div className={helpClass}>{help}</div>
      )}
    </div>
  )
}

export const SafetyScreen = ({
  options,
  selected,
  onToggle
}: {
  options: { id?: string; v: unknown; l: string }[]
  selected: string[]
  onToggle: (id: string) => void
}) => {
  const idOf = (option: { id?: string; v: unknown }) => String(option.id ?? option.v)
  const conditions = options.filter((option) => !['none', 'unknown'].includes(idOf(option)))
  const exclusive = options.filter((option) => ['none', 'unknown'].includes(idOf(option)))

  const renderOption = (option: { id?: string; v: unknown; l: string }) => {
    const id = idOf(option)
    return (
      <CheckCard key={id} active={selected.includes(id)} onSelect={() => onToggle(id)}>
        {option.l}
      </CheckCard>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-5">
      <TrailHeading
        eyebrow="Sua segurança"
        title="Você possui alguma das condições abaixo?"
        sub="Selecione todas as opções aplicáveis."
      />
      <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3">
        {conditions.map(renderOption)}
        <hr className="my-1 border-0 border-t border-solid border-[#EEEEEE] md:col-span-2" />
        {exclusive.map(renderOption)}
      </div>
    </div>
  )
}

const OFFER_TILES = [
  { value: '3', label: 'meses de tratamento' },
  { icon: <Stethoscope className="size-5" />, label: 'consulta médica por teleatendimento' },
  { value: '2', label: 'consultas com nutricionista' },
  { value: '1', label: 'consulta com psicólogo' },
  { icon: <Activity className="size-5" />, label: 'enfermagem contínua' },
  {
    icon: <Pill className="size-5" />,
    label: (
      <>
        <TirzepatidaLink />, quando indicada
      </>
    )
  }
] as const

const JOURNEY = [
  {
    icon: <Star className="size-4" />,
    title: 'Você começa por aqui',
    text: 'Conheça o programa, escolha como prefere pagar e dê o primeiro passo. Tudo pelo celular, no seu ritmo.'
  },
  {
    icon: <Stethoscope className="size-4" />,
    title: 'Uma conversa com o médico',
    text: 'Você escolhe o dia e o horário e conta um pouco sobre a sua história. O médico chega à consulta já conhecendo você.'
  },
  {
    icon: <Package className="size-4" />,
    title: 'O tratamento chega até você',
    text: 'Se o médico indicar, você confirma o endereço, envia os documentos e recebe tudo em casa.'
  },
  {
    icon: <TrendingUp className="size-4" />,
    title: 'Uma equipe ao seu lado',
    text: 'Nutricionista, psicólogo e enfermagem acompanham você em cada fase, para você não seguir sozinho.'
  }
] as const

const INCLUDED_CARDS = [
  {
    icon: <Stethoscope className="size-5" />,
    title: 'Consulta médica por teleatendimento',
    text: 'O médico avalia o seu caso e acompanha a sua evolução durante os 3 meses'
  },
  {
    icon: <Apple className="size-5" />,
    title: 'Nutricionista',
    text: '2 consultas para ajustar sua alimentação à sua rotina'
  },
  { icon: <Heart className="size-5" />, title: 'Psicólogo', text: '1 consulta para cuidar também da parte emocional' },
  {
    icon: <MessageCircleHeart className="size-5" />,
    title: 'Enfermagem',
    text: 'Apoio contínuo para tirar dúvidas e acompanhar sua rotina'
  }
] as const

const SupportLinks = () => (
  <p className="m-0 text-[13px] leading-snug text-[#433F3E]">
    Ficou com dúvidas? Veja nossas{' '}
    <a
      href="/programa-perda-peso#duvidas"
      target="_blank"
      rel="noreferrer"
      className="font-bold text-marketplace-900 underline">
      perguntas frequentes
    </a>{' '}
    ou{' '}
    <a
      href={SUPPORT_WHATSAPP_URL}
      target="_blank"
      rel="noreferrer"
      className="font-bold text-marketplace-900 underline">
      fale com nosso time
    </a>
    .
  </p>
)

const REFUND_NOTICE =
  'A consulta médica não garante a prescrição. Se o médico não indicar o tratamento, você recebe o reembolso do valor pago, descontado o valor da consulta.'

const OfferPrice = () => (
  <>
    <p className="m-0 mt-1 text-[28px] font-bold leading-none text-[#1A1A1A] md:mt-0 md:text-[34px]">
      {formatBrl(PROGRAM_INSTALLMENT_VALUE).replace(',00', '')}{' '}
      <span className="text-sm font-semibold md:font-medium md:text-[#433F3E]">por mês</span>
    </p>
    <p className="m-0 mt-1.5 text-xs text-[#433F3E] md:mt-2 md:text-[13px]">
      Total de {formatBrl(PROGRAM_PRICE).replace(',00', '')}, divididos em {PROGRAM_INSTALLMENTS} vezes no cartão
    </p>
    <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-white p-2.5 text-[13px] text-[#1A1A1A] md:mt-4 md:items-start md:bg-marketplace-100 md:p-3">
      <span className="rounded-full bg-marketplace-300 px-2.5 py-1 text-xs font-bold text-marketplace-900 md:bg-marketplace-800 md:text-white">
        -{PROGRAM_PIX_DISCOUNT_PERCENT}%
      </span>
      <span>
        Pagando no Pix, você ganha {PROGRAM_PIX_DISCOUNT_PERCENT}% de desconto:{' '}
        <strong>{formatBrl(PROGRAM_PIX_PRICE)} à vista.</strong>
      </span>
    </div>
  </>
)

// `actions` são os mesmos botões do rodapé; no desktop eles ficam no card de preço fixo ao lado do conteúdo.
export const OfferScreen = ({ actions }: { actions?: ReactNode }) => (
  <div className="flex flex-1 flex-col gap-5 pb-2 md:grid md:flex-none md:grid-cols-[minmax(0,1fr)_320px] md:items-start md:gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
    <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-marketplace-900 to-marketplace-700 p-5 text-white md:col-span-2 md:rounded-3xl md:p-8">
      <span className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full border border-solid border-white/15" />
      <span className="pointer-events-none absolute -right-8 -top-8 size-32 rounded-full border border-solid border-white/15" />
      <h1 className="relative m-0 text-2xl font-bold leading-tight md:text-[32px]">
        Programa completo de emagrecimento
      </h1>
      <p className="relative m-0 mt-3 text-sm leading-[1.5] text-white/90 md:max-w-[560px] md:text-base">
        Um cuidado completo, com médico, nutricionista, psicólogo e enfermagem ao seu lado durante os {PROGRAM_MONTHS}{' '}
        meses, e medicamento quando indicado pelo médico.
      </p>
      <div className="relative mt-4 grid grid-cols-2 gap-2.5 md:mt-6 md:grid-cols-3 md:gap-3 lg:grid-cols-6">
        {OFFER_TILES.map((tile, index) => (
          <div key={index} className="flex min-h-[84px] flex-col justify-between gap-2 rounded-xl bg-white/10 p-3">
            {'icon' in tile ? (
              <span className="text-white">{tile.icon}</span>
            ) : (
              <span className="text-2xl font-bold leading-none">{tile.value}</span>
            )}
            <span className="text-xs leading-tight text-white/85">{tile.label}</span>
          </div>
        ))}
      </div>
    </section>

    <section className="rounded-2xl border border-solid border-marketplace-300 bg-marketplace-100 p-4 md:hidden">
      <p className="m-0 text-xs font-medium text-[#433F3E]">Programa de {PROGRAM_MONTHS} meses</p>
      <OfferPrice />
    </section>

    <div className="flex min-w-0 flex-col gap-5">
      <section className="md:rounded-2xl md:border md:border-solid md:border-[#ECECEC] md:bg-white md:p-6">
        <h2 className="m-0 text-lg font-bold text-[#1A1A1A]">Como será a sua jornada</h2>
        <p className={cn(bodyClass, 'mt-0.5')}>Quatro passos, com cuidado de perto em todos eles.</p>
        <ol className={cn(cardClass, 'relative m-0 mt-3 flex list-none flex-col gap-5 p-4 md:mt-5 md:border-0 md:p-0')}>
          <span className="pointer-events-none absolute bottom-10 left-[31px] top-10 w-px bg-marketplace-300 md:bottom-8 md:left-[15px] md:top-8" />
          {JOURNEY.map((step, index) => (
            <li key={step.title} className="relative flex gap-3 md:gap-4">
              <span
                className={cn(
                  'z-[1] inline-flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-solid',
                  index === JOURNEY.length - 1
                    ? 'border-marketplace-800 bg-white text-marketplace-800'
                    : 'border-marketplace-800 bg-marketplace-800 text-white'
                )}>
                {step.icon}
              </span>
              <span>
                <p className="m-0 hidden text-xs font-semibold text-marketplace-800 md:block">Passo {index + 1}</p>
                <p className="m-0 text-sm font-semibold text-[#1A1A1A] md:text-base">{step.title}</p>
                <p className="m-0 mt-0.5 text-[13px] leading-[1.45] text-[#433F3E] md:text-sm">{step.text}</p>
              </span>
            </li>
          ))}
        </ol>
      </section>

      <section className="md:rounded-2xl md:border md:border-solid md:border-[#ECECEC] md:bg-white md:p-6">
        <h2 className="m-0 mb-3 text-lg font-bold text-[#1A1A1A] md:mb-2">O que está incluso</h2>
        <div className="grid grid-cols-2 gap-2.5 md:flex md:flex-col md:gap-0">
          {INCLUDED_CARDS.map((item) => (
            <div
              key={item.title}
              className={cn(
                cardClass,
                'flex flex-col gap-2 p-3.5 md:flex-row md:items-start md:gap-3 md:rounded-none md:border-0 md:border-t md:px-0 md:py-4'
              )}>
              <IconTile className="size-9 rounded-lg">{item.icon}</IconTile>
              <div className="flex flex-col gap-2 md:gap-0.5">
                <p className="m-0 text-sm font-semibold leading-snug text-[#1A1A1A] md:text-[15px]">{item.title}</p>
                <p className="m-0 text-xs leading-snug text-[#433F3E] md:text-[13px]">{item.text}</p>
              </div>
            </div>
          ))}
          <div
            className={cn(
              cardClass,
              'col-span-2 flex flex-col gap-2 p-3.5 md:flex-row md:items-start md:gap-3 md:rounded-none md:border-0 md:border-t md:px-0 md:pb-0 md:pt-4'
            )}>
            <IconTile className="size-9 rounded-lg">
              <Pill className="size-5" />
            </IconTile>
            <div className="flex flex-col gap-2 md:gap-0.5">
              <p className="m-0 text-sm font-semibold text-[#1A1A1A] md:text-[15px]">
                <TirzepatidaLink>{TIRZEPATIDA_NAME}</TirzepatidaLink>
              </p>
              <p className="m-0 text-xs text-[#433F3E] md:text-[13px]">
                Medicamento manipulado, quando indicado pelo médico
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={cn(cardClass, 'flex flex-col gap-3 p-4 md:hidden')}>
        <h2 className="m-0 text-sm font-semibold text-[#1A1A1A]">Como funciona o pagamento</h2>
        <p className={bodyClass}>
          São {formatBrl(PROGRAM_INSTALLMENT_VALUE).replace(',00', '')} por mês, em {PROGRAM_INSTALLMENTS} vezes no
          cartão, ou {formatBrl(PROGRAM_PIX_PRICE)} à vista no Pix, com {PROGRAM_PIX_DISCOUNT_PERCENT}% de desconto.
        </p>
        <hr className="m-0 border-0 border-t border-solid border-[#EEEEEE]" />
        <p className={bodyClass}>{REFUND_NOTICE}</p>
      </section>

      <div className="md:hidden">
        <SupportLinks />
      </div>
    </div>

    <aside className="sticky top-0 hidden overflow-hidden rounded-2xl border border-solid border-marketplace-800 bg-white shadow-[0_12px_32px_rgba(42,47,29,0.12)] md:block">
      <p className="m-0 bg-marketplace-800 px-5 py-2.5 text-[13px] font-semibold text-white">
        Programa de {PROGRAM_MONTHS} meses
      </p>
      <div className="flex flex-col p-5">
        <OfferPrice />
        {actions ? (
          // O "Agora não" vira botão contornado dentro do card.
          <div className="mt-5 flex flex-col [&>button:nth-child(2)]:mt-2.5 [&>button:nth-child(2)]:h-12 [&>button:nth-child(2)]:rounded-full [&>button:nth-child(2)]:border [&>button:nth-child(2)]:border-solid [&>button:nth-child(2)]:border-[#DADADA] [&>button:nth-child(2)]:font-semibold [&>button:nth-child(2)]:hover:bg-[#F7F7F7]">
            {actions}
          </div>
        ) : null}
        <p className="m-0 mt-4 text-xs leading-[1.5] text-[#6B6560]">* {REFUND_NOTICE}</p>
        <hr className="my-4 w-full border-0 border-t border-solid border-[#EEEEEE]" />
        <SupportLinks />
      </div>
    </aside>
  </div>
)

export const DeclineScreen = ({ value, onToggle }: { value: string[]; onToggle: (value: string) => void }) => (
  <div className="flex flex-1 flex-col gap-5">
    <TrailHeading
      eyebrow="Antes de você ir"
      title="Pode nos contar por que prefere não seguir agora?"
      sub="Selecione um ou mais motivos. Isso nos ajuda a melhorar."
    />
    <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-marketplace-900 to-marketplace-700 p-4 text-white">
      <span className="pointer-events-none absolute -right-10 -top-10 size-32 rounded-full bg-white/10" />
      <div className="relative flex items-start gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-white text-marketplace-900">
          <MessageCircleHeart className="size-4" />
        </span>
        <div>
          <p className="m-0 text-sm font-semibold">Que tal conversar antes de decidir?</p>
          <p className="m-0 mt-1 text-xs leading-snug text-white/90">{DECLINE_HELP_TEXT}</p>
        </div>
      </div>
      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        className="relative mt-4 inline-flex h-11 w-full items-center justify-center rounded-full bg-white text-sm font-semibold text-marketplace-900 no-underline">
        Falar com a equipe
      </a>
    </section>
    <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3">
      {DECLINE_OPTIONS.map((option) => (
        <CheckCard key={option.id} active={value.includes(option.id)} onSelect={() => onToggle(option.id)}>
          {option.label}
        </CheckCard>
      ))}
    </div>
  </div>
)

export type TProgramPaymentMethod = 'pix' | 'credit'

export const PaymentMethodPicker = ({
  value,
  onChange
}: {
  value: TProgramPaymentMethod
  onChange: (value: TProgramPaymentMethod) => void
}) => {
  const options = [
    {
      id: 'pix' as const,
      icon: <FaPix className="size-4" />,
      title: 'Pix',
      sub: 'Aprovação na hora',
      amount: formatBrl(PROGRAM_PIX_PRICE),
      badge: `${PROGRAM_PIX_DISCOUNT_PERCENT}% de desconto pagando no Pix`
    },
    {
      id: 'credit' as const,
      icon: <CreditCard className="size-4" />,
      title: 'Cartão de crédito',
      sub: `Em ${PROGRAM_INSTALLMENTS}x de ${formatBrl(PROGRAM_INSTALLMENT_VALUE)}`,
      amount: formatBrl(PROGRAM_PRICE),
      badge: ''
    }
  ]

  return (
    <div className="flex flex-col gap-2.5">
      {options.map((option) => {
        const active = value === option.id
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={cn(
              'flex w-full cursor-pointer items-start gap-3 rounded-2xl border border-solid p-3.5 text-left',
              active ? 'border-marketplace-800 bg-marketplace-200' : 'border-[#E8E8E8] bg-white'
            )}>
            <span className="mt-2.5">
              <RadioMark active={active} />
            </span>
            <IconTile className={cn('size-10 rounded-xl', active ? 'bg-white' : 'bg-marketplace-200')}>
              {option.icon}
            </IconTile>
            <span className="flex-1">
              <span className="flex items-start justify-between gap-2">
                <span>
                  <span className="block text-sm font-semibold text-[#1A1A1A]">{option.title}</span>
                  <span className="block text-xs text-[#433F3E]">{option.sub}</span>
                </span>
                <span className="text-sm font-bold text-[#1A1A1A]">{option.amount}</span>
              </span>
              {option.badge ? (
                <span className="mt-2 inline-flex rounded-full bg-marketplace-300 px-2.5 py-1 text-[11px] font-semibold text-marketplace-900">
                  {option.badge}
                </span>
              ) : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

const BIND_INCLUDED = [
  {
    icon: <CalendarCheck className="size-[18px]" />,
    title: 'Consulta médica por teleatendimento',
    text: `Para avaliar, indicar o tratamento e acompanhar você durante os ${PROGRAM_MONTHS} meses`
  },
  {
    icon: <Pill className="size-[18px]" />,
    title: <TirzepatidaLink />,
    text: `Manipulado, quando indicado pelo médico · duração de ${PROGRAM_MONTHS} meses`,
    highlight: true
  },
  {
    icon: <Apple className="size-[18px]" />,
    title: '2 consultas com nutricionista',
    text: 'Para ajustar sua alimentação à sua rotina'
  },
  {
    icon: <Heart className="size-[18px]" />,
    title: '1 consulta com psicólogo',
    text: 'Para cuidar também da parte emocional'
  },
  {
    icon: <MessageCircleHeart className="size-[18px]" />,
    title: 'Enfermagem contínua',
    text: 'Para tirar dúvidas e apoiar sua rotina'
  }
] as const

// Resumo do programa no carrinho da trilha: lista tudo o que está incluso, no lugar da linha única do produto.
export const ProgramIncludedCard = () => (
  <section className={cn(cardClass, 'p-4')}>
    <div className="flex items-center gap-3">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-marketplace-900 text-white">
        <Star className="size-5" />
      </span>
      <div className="flex-1">
        <p className="m-0 text-sm font-semibold leading-snug text-[#1A1A1A]">Programa Perda de Peso</p>
        <p className="m-0 text-xs text-[#6B6560]">{PROGRAM_MONTHS} meses de acompanhamento</p>
      </div>
      <p className="m-0 text-sm font-bold text-[#1A1A1A]">
        {formatBrl(PROGRAM_INSTALLMENT_VALUE).replace(',00', '')}
        <span className="text-xs font-normal">/mês</span>
      </p>
    </div>
    <hr className="my-4 border-0 border-t border-solid border-[#EEEEEE]" />
    <h2 className="m-0 mb-3 text-[13px] font-semibold text-[#1A1A1A]">O que está incluso no programa</h2>
    <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
      {BIND_INCLUDED.map((item) => (
        <li
          key={item.text}
          className={cn(
            'flex items-start gap-3 rounded-xl p-2',
            'highlight' in item && 'border border-solid border-marketplace-300 bg-marketplace-100'
          )}>
          <IconTile className={cn('size-10 rounded-xl', 'highlight' in item && 'bg-marketplace-900 text-white')}>
            {item.icon}
          </IconTile>
          <span className="flex-1">
            <p className="m-0 text-[13px] font-semibold leading-snug text-[#1A1A1A]">{item.title}</p>
            <p className="m-0 text-[11px] leading-snug text-[#433F3E]">{item.text}</p>
          </span>
          <span className="text-[10px] font-semibold text-marketplace-900">Incluso</span>
        </li>
      ))}
    </ul>
  </section>
)

export const BindScreen = ({
  name,
  birthday,
  cpf,
  email,
  cpfLocked,
  emailLocked,
  onCpf,
  onEmail,
  error
}: {
  name?: string
  birthday?: string
  cpf: string
  email: string
  cpfLocked?: boolean
  emailLocked?: boolean
  onCpf: (value: string) => void
  onEmail: (value: string) => void
  error?: string
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <TrailHeading eyebrow="Pagamento" title="Seus dados" />

    <div className="flex flex-col gap-4">
      <TrailHeading
        title="Dados do paciente"
        sub="Nome e nascimento vieram do início. Informe o CPF e o e-mail para emitir o pagamento e a nota fiscal."
      />
      {name ? <TrailField label="Nome completo" value={name} readOnly /> : null}
      {birthday ? <TrailField label="Data de nascimento" value={birthday} readOnly /> : null}
      <TrailField
        label="CPF"
        value={cpf}
        onChange={onCpf}
        inputMode="numeric"
        placeholder="000.000.000-00"
        readOnly={cpfLocked}
        hint="Você só informa uma vez. Ele já aparece preenchido no agendamento."
        error={!cpfLocked && cpf.replace(/\D/g, '').length === 11 && !isValidCpf(cpf) ? CPF_ERROR : undefined}
      />
      <TrailField
        label="E-mail"
        value={email}
        onChange={onEmail}
        type="email"
        placeholder="voce@email.com"
        readOnly={emailLocked}
        hint="Enviamos a confirmação do pagamento e os próximos passos."
        error={!emailLocked && email.includes('@') && !isValidEmail(email) ? EMAIL_ERROR : undefined}
      />
    </div>

    <p className="m-0 text-xs leading-snug text-[#433F3E]">
      A consulta não garante a prescrição. Se o médico não indicar o tratamento, você recebe o reembolso do valor pago,
      descontado o valor da consulta.
    </p>

    <section className="flex flex-col items-stretch gap-3 rounded-2xl border border-solid border-[#ECECEC] bg-[#F7F8FA] p-4">
      <div>
        <p className="m-0 text-sm font-semibold text-[#1A1A1A]">Ficou com dúvidas?</p>
        <p className="m-0 mt-0.5 text-xs leading-snug text-[#433F3E]">
          Tire suas dúvidas antes de pagar. Nossa equipe responde rapidinho.
        </p>
      </div>
      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-solid border-marketplace-800 bg-white text-sm font-semibold text-marketplace-900 no-underline">
        <MessageCircle className="size-4" />
        Falar com a equipe
      </a>
      <a
        href="/programa-perda-peso#duvidas"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center gap-2 text-[13px] font-medium text-[#1A1A1A] no-underline">
        <CircleHelp className="size-4" />
        Ver perguntas frequentes
      </a>
    </section>

    {error ? <p className="m-0 text-sm text-[#DC4E5A]">{error}</p> : null}
  </div>
)

const summaryAmountLabel = (summary: TProgramPaymentSummary | null) => {
  if (!summary) return '—'
  const amount = formatBrl(summary.amount)
  return summary.method === 'credit' && summary.installments && summary.installments > 1
    ? `${amount} em ${summary.installments}x`
    : amount
}

const summaryMethodLabel = (summary: TProgramPaymentSummary | null) => {
  if (!summary) return '—'
  return summary.method === 'pix' ? 'Pix' : 'Cartão de crédito'
}

const SummaryRow = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className="flex items-baseline justify-between gap-4 text-[13px]">
    <span className="text-[#6B6560]">{label}</span>
    <span className={cn('text-right text-[#1A1A1A]', strong ? 'text-sm font-bold' : 'font-semibold')}>{value}</span>
  </div>
)

// Pagamento recebido, esperando a API reconhecê-lo (postback da Pagar.me): evita voltar para a oferta.
export const PaymentConfirmingScreen = () => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
    <Loader2 className="size-8 animate-spin text-marketplace-800" aria-hidden />
    <h1 className={titleClass}>Confirmando o seu pagamento</h1>
    <p className={bodyClass}>
      Recebemos o seu pagamento e estamos liberando o programa. Isso leva alguns segundos; não feche esta tela.
    </p>
  </div>
)

export const PaymentConfirmedScreen = ({ summary }: { summary: TProgramPaymentSummary | null }) => (
  <div className="flex flex-1 flex-col items-center gap-5 pt-10 text-center">
    <span className="inline-flex size-16 items-center justify-center rounded-2xl bg-marketplace-200 text-marketplace-900">
      <CircleCheck className="size-8" />
    </span>
    <div className="flex flex-col gap-2">
      <h1 className={titleClass}>Pagamento confirmado!</h1>
      <p className={cn(bodyClass, 'mx-auto max-w-[300px]')}>
        Seu programa está garantido. Agora é só escolher o dia e o horário da sua consulta médica.
      </p>
    </div>
    <section className="flex w-full flex-col gap-2.5 rounded-2xl border border-solid border-[#ECECEC] bg-[#F7F8FA] p-4 text-left">
      <h2 className="m-0 mb-1 text-[13px] font-semibold text-[#1A1A1A]">Resumo do pagamento</h2>
      <SummaryRow label="Programa" value="Perda de Peso" />
      <SummaryRow label="Duração" value={`${PROGRAM_MONTHS} meses`} />
      <SummaryRow label="Forma de pagamento" value={summaryMethodLabel(summary)} />
      <hr className="my-1 border-0 border-t border-solid border-[#E4E4E4]" />
      <SummaryRow label="Valor pago" value={summaryAmountLabel(summary)} strong />
    </section>
    <div className="flex w-full items-start gap-2.5 rounded-xl border border-solid border-marketplace-300 bg-marketplace-100 px-3.5 py-3 text-left text-[13px] leading-snug text-[#1A1A1A]">
      <Info className="mt-0.5 size-4 shrink-0 text-marketplace-800" />
      <p className="m-0">
        <strong className="font-semibold">Próximo passo:</strong> agende sua consulta médica. Depois, você responde as
        perguntas para o médico se preparar.
      </p>
    </div>
  </div>
)

export const PaymentFailedScreen = ({ summary }: { summary: TProgramPaymentSummary | null }) => (
  <div className="flex flex-1 flex-col items-center gap-5 pt-4 text-center">
    <span className="inline-flex size-16 items-center justify-center rounded-2xl bg-[#FDECEE] text-[#DC4E5A]">
      <CircleAlert className="size-8" />
    </span>
    <div className="flex flex-col gap-2">
      <h1 className={titleClass}>Não conseguimos confirmar seu pagamento</h1>
      <p className={cn(bodyClass, 'mx-auto max-w-[300px]')}>
        Nenhum valor foi cobrado. Você pode tentar de novo ou escolher outra forma de pagamento.
      </p>
    </div>
    <section className="flex w-full flex-col gap-2.5 rounded-2xl border border-solid border-[#ECECEC] bg-[#F7F8FA] p-4 text-left">
      <SummaryRow label="Programa" value={`Programa de ${PROGRAM_MONTHS} meses`} />
      <SummaryRow label="Forma de pagamento" value={summaryMethodLabel(summary)} />
      <SummaryRow label="Valor" value={summaryAmountLabel(summary)} />
    </section>
    <p className="m-0 text-[13px] text-[#433F3E]">
      Precisa de ajuda?{' '}
      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        className="font-bold text-marketplace-900 underline">
        Fale com nosso time
      </a>
    </p>
  </div>
)

export const ClinicalIntroScreen = () => (
  <div className="flex flex-1 flex-col gap-5 md:mx-auto md:w-full md:max-w-[960px] md:flex-none md:gap-7 md:rounded-3xl md:border md:border-solid md:border-[#ECECEC] md:bg-white md:p-8 md:shadow-[0_8px_32px_rgba(26,26,26,0.06)]">
    <TrailPill icon={<HeartPulse className="size-3.5" />}>Seu programa de saúde começa agora</TrailPill>
    <TrailHeading
      title="Agora responda as perguntas que serão enviadas para seu médico"
      sub="São perguntas sobre seu peso, sua saúde, os medicamentos que você usa e sua rotina. Quanto mais completas forem suas respostas, mais a consulta pode focar no que importa para você."
    />
    <section
      className={cn(cardClass, 'flex flex-col gap-4 p-4 md:grid md:grid-cols-3 md:border-0 md:bg-transparent md:p-0')}>
      {[
        {
          icon: <Stethoscope className="size-4" />,
          title: 'O médico chega preparado',
          text: 'Ele lê suas respostas antes da consulta e já conhece seu histórico.'
        },
        {
          icon: <Heart className="size-4" />,
          title: 'Um plano pensado para você',
          text: 'Com mais informações, o acompanhamento fica mais seguro e mais certeiro.'
        },
        {
          icon: <Lock className="size-4" />,
          title: 'Suas respostas são protegidas',
          text: 'Elas são usadas apenas pela equipe de saúde que vai cuidar de você.'
        }
      ].map((item) => (
        <div
          key={item.title}
          className="flex items-start gap-3 md:flex-col md:gap-4 md:rounded-2xl md:bg-[#F7F7F5] md:p-5">
          <IconTile className="size-8 rounded-lg md:size-10 md:rounded-xl">{item.icon}</IconTile>
          <div className="md:flex md:flex-col md:gap-1.5">
            <p className="m-0 text-sm font-semibold text-[#1A1A1A] md:text-base">{item.title}</p>
            <p className="m-0 text-xs leading-snug text-[#433F3E] md:text-sm md:leading-[1.5]">{item.text}</p>
          </div>
        </div>
      ))}
    </section>
    <div className="md:border-0 md:border-t md:border-solid md:border-[#EEEEEE] md:pt-6">
      <p className="m-0 inline-flex items-center gap-2 text-xs text-[#433F3E] md:text-sm">
        <Clock className="size-4 text-[#6B6560]" />
        São 13 perguntas e leva poucos minutos.
      </p>
    </div>
  </div>
)

const ClinicalHeader = ({
  index,
  total,
  eyebrow,
  title,
  sub
}: {
  index: number
  total: number
  eyebrow?: string
  title: string
  sub?: string | null
}) => (
  <div className="flex flex-col gap-3">
    {index > 0 ? (
      <TrailPill icon={<Stethoscope className="size-3.5" />}>
        Para o médico · {index} de {total}
      </TrailPill>
    ) : null}
    <TrailHeading eyebrow={eyebrow} title={title} sub={sub} />
  </div>
)

export const OptionsScreen = ({
  title,
  sub,
  eyebrow,
  index,
  total,
  options,
  selected,
  onSelect
}: {
  title: string
  sub?: string | null
  eyebrow?: string
  index: number
  total: number
  options: { v: unknown; l: string }[]
  selected: string
  onSelect: (value: unknown) => void
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <ClinicalHeader index={index} total={total} eyebrow={eyebrow} title={title} sub={sub} />
    <div className="flex flex-col gap-2.5">
      {options.map((option) => (
        <RadioCard key={String(option.v)} active={selected === String(option.v)} onSelect={() => onSelect(option.v)}>
          {option.l}
        </RadioCard>
      ))}
    </div>
  </div>
)

export const YnDetailScreen = ({
  title,
  sub,
  eyebrow,
  index,
  total,
  value,
  detail,
  onValue,
  onDetail
}: {
  title: string
  sub?: string | null
  eyebrow?: string
  index: number
  total: number
  value: number | null
  detail: string
  onValue: (value: number) => void
  onDetail: (value: string) => void
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <ClinicalHeader index={index} total={total} eyebrow={eyebrow} title={title} sub={sub} />
    <div className="flex flex-col gap-2.5">
      {[
        { v: 1, l: 'Sim' },
        { v: 0, l: 'Não' }
      ].map((option) => (
        <RadioCard key={option.v} active={value === option.v} onSelect={() => onValue(option.v)}>
          {option.l}
        </RadioCard>
      ))}
    </div>
    {value === 1 ? (
      <textarea
        className={cn(inputClass, 'h-28 py-3')}
        value={detail}
        onChange={(event) => onDetail(event.target.value)}
        placeholder="Descreva brevemente"
      />
    ) : null}
  </div>
)

export const ClinicalMultiScreen = ({
  title,
  sub,
  eyebrow,
  index,
  total,
  options,
  selected,
  max,
  onToggle
}: {
  title: string
  sub?: string | null
  eyebrow?: string
  index: number
  total: number
  options: { id?: string; v: unknown; l: string; exclusive?: boolean }[]
  selected: string[]
  max?: number | null
  onToggle: (id: string) => void
}) => {
  const idOf = (option: { id?: string; v: unknown }) => String(option.id ?? option.v)
  const exclusivePicked = options.some((option) => option.exclusive && selected.includes(idOf(option)))
  const othersPicked = selected.some((id) => !options.find((option) => idOf(option) === id)?.exclusive)

  return (
    <div className="flex flex-1 flex-col gap-5">
      <ClinicalHeader index={index} total={total} eyebrow={eyebrow} title={title} sub={sub} />
      <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3">
        {options.map((option) => {
          const id = idOf(option)
          const active = selected.includes(id)
          // "Nenhuma condição" só vale sozinha; as demais opções ficam inativas enquanto ela está marcada.
          const blocked =
            !active &&
            ((exclusivePicked && !option.exclusive) ||
              (Boolean(option.exclusive) && othersPicked) ||
              (!option.exclusive && Boolean(max) && selected.length >= (max as number)))

          return (
            <div key={id} className={cn(blocked && 'pointer-events-none opacity-50')} aria-disabled={blocked}>
              <CheckCard active={active} onSelect={() => onToggle(id)}>
                {option.l}
              </CheckCard>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const ClinicalTextScreen = ({
  title,
  sub,
  eyebrow,
  index,
  total,
  value,
  placeholder,
  onChange
}: {
  title: string
  sub?: string | null
  eyebrow?: string
  index: number
  total: number
  value: string
  placeholder?: string
  onChange: (value: string) => void
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <ClinicalHeader index={index} total={total} eyebrow={eyebrow} title={title} sub={sub} />
    <div className="flex flex-col gap-2">
      <p className="m-0 text-sm font-medium text-[#1A1A1A]">Sua resposta</p>
      <textarea
        aria-label="Sua resposta"
        className={cn(inputClass, 'h-32 py-3 font-normal')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder || 'Escreva aqui'}
      />
    </div>
  </div>
)

export const PrescriberAvatar = ({ name }: { name: string }) => {
  const initials = name
    .replace(/^(dr|dra)\.?\s+/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-marketplace-300 text-sm font-semibold text-marketplace-900">
      {initials}
    </span>
  )
}

export type TConfirmData = { name: string; birthday: string; cpf: string; email: string }

export const ConfirmDataScreen = ({
  prescriber,
  slotLabel,
  values,
  locked,
  errors,
  onChange,
  onChangeSlot
}: {
  prescriber: string
  slotLabel: string
  values: TConfirmData
  // Campos já cadastrados na identificação/pagamento: só leitura. Os que vieram vazios seguem editáveis.
  locked: Partial<Record<keyof TConfirmData, boolean>>
  errors: Partial<Record<keyof TConfirmData, string>>
  onChange: (field: keyof TConfirmData, value: string) => void
  onChangeSlot: () => void
}) => (
  <div className="flex flex-1 flex-col gap-5">
    <TrailHeading
      eyebrow="Agendamento"
      title="Seus dados para a consulta"
      sub="Confira seus dados, cadastrados no pagamento."
    />
    <section className={cn(cardClass, 'flex items-center gap-3 bg-[#F7F8FA] p-3.5')}>
      <PrescriberAvatar name={prescriber} />
      <div className="flex-1">
        <p className="m-0 text-sm font-semibold text-[#1A1A1A]">{prescriber}</p>
        <p className="m-0 text-xs leading-snug text-[#433F3E]">{slotLabel}</p>
      </div>
      <button
        type="button"
        onClick={onChangeSlot}
        className="border-0 bg-transparent p-0 text-xs font-bold text-marketplace-900 underline">
        Alterar
      </button>
    </section>
    <div className="flex flex-col gap-1">
      <p className="m-0 text-sm font-semibold text-[#1A1A1A]">Seus dados</p>
      <p className="m-0 text-xs text-[#6B6560]">
        Preenchidos com o que você informou antes e não podem ser alterados aqui.
      </p>
    </div>
    <TrailField
      label="Nome completo"
      value={values.name}
      onChange={(value) => onChange('name', value)}
      readOnly={locked.name}
      error={errors.name}
    />
    <TrailField
      label="Data de nascimento"
      value={values.birthday}
      onChange={(value) => onChange('birthday', value)}
      inputMode="numeric"
      placeholder="DD/MM/AAAA"
      readOnly={locked.birthday}
      error={errors.birthday}
    />
    <TrailField
      label="CPF"
      value={values.cpf}
      onChange={(value) => onChange('cpf', value)}
      inputMode="numeric"
      placeholder="000.000.000-00"
      hint="Precisa ser o mesmo CPF informado no pagamento."
      readOnly={locked.cpf}
      error={errors.cpf}
    />
    <TrailField
      label="E-mail"
      value={values.email}
      onChange={(value) => onChange('email', value)}
      type="email"
      readOnly={locked.email}
      error={errors.email}
    />
  </div>
)

export const ScheduledScreen = ({ slotLabel }: { slotLabel: string }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
    <span className="inline-flex size-16 items-center justify-center rounded-2xl bg-marketplace-200 text-marketplace-900">
      <CalendarCheck className="size-8" />
    </span>
    <h1 className={titleClass}>Consulta agendada!</h1>
    <p className={cn(bodyClass, 'max-w-[280px]')}>{slotLabel}. Você vai receber a confirmação por e-mail.</p>
  </div>
)

export type TNewAccountNotice = {
  email: string
  sent: boolean
  sending: boolean
  failed: boolean
  cooldown: number
  onResend: () => void
}

const resendLabel = ({ sending, cooldown, sent }: TNewAccountNotice) => {
  if (sending) return 'Enviando…'
  if (cooldown > 0) return `Reenviar e-mail em ${cooldown}s`
  return sent ? 'Reenviar e-mail' : 'Enviar e-mail'
}

export const DoneScreen = ({ appointment, newAccount }: { appointment?: string; newAccount?: TNewAccountNotice }) => (
  <div
    className={cn(
      'flex flex-1 flex-col items-center justify-center gap-4 text-center',
      newAccount && 'md:grid md:flex-none md:grid-cols-2 md:items-center md:gap-14 md:py-6 md:text-left'
    )}>
    <div className={cn('flex flex-col items-center gap-4 md:gap-5', newAccount && 'md:items-start')}>
      <span className="inline-flex size-16 items-center justify-center rounded-2xl bg-marketplace-200 text-marketplace-900 md:size-20 md:rounded-3xl">
        <CircleCheck className="size-8 md:size-10" />
      </span>
      <h1 className={cn(titleClass, 'md:text-[40px]')}>Respostas enviadas!</h1>
      <p className={cn(bodyClass, 'max-w-[300px] md:max-w-[520px] md:text-lg')}>
        {appointment
          ? `Seu médico vai receber suas respostas antes da consulta de ${appointment}.`
          : 'Seu médico vai receber suas respostas antes da consulta.'}
      </p>
    </div>
    {newAccount ? (
      <section className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-marketplace-900 to-marketplace-700 p-5 text-left text-white shadow-[0_12px_28px_rgba(59,73,31,.28)] md:max-w-[560px] md:rounded-3xl md:p-8">
        <span className="pointer-events-none absolute -right-10 -top-10 size-32 rounded-full border border-solid border-white/15" />
        <div className="relative flex items-start gap-3">
          <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
            <KeyRound className="size-5" />
          </span>
          <div>
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[1px] text-white/75">Ação necessária</p>
            <h2 className="m-0 text-lg font-bold leading-tight md:text-[22px]">
              Defina a sua senha para acessar a Cannect
            </h2>
          </div>
        </div>
        <p className="relative m-0 mt-3 text-[13px] leading-snug text-white/90 md:mt-4 md:text-[15px]">
          Criamos o seu acesso para você acompanhar a consulta e o programa. Falta só criar uma senha:
        </p>
        <ol className="relative m-0 mt-3 flex list-none flex-col gap-2 p-0 text-[13px] leading-snug md:mt-4 md:gap-3 md:text-[15px]">
          <li className="flex items-start gap-2.5">
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-marketplace-900">
              1
            </span>
            <span>
              Abra o e-mail que enviamos para{' '}
              <strong className="break-all rounded-md bg-white/15 px-1.5 py-0.5">{newAccount.email}</strong>
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-marketplace-900">
              2
            </span>
            <span>Clique no link e escolha a sua senha</span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-marketplace-900">
              3
            </span>
            <span>Volte aqui e entre com o seu e-mail e a nova senha</span>
          </li>
        </ol>
        <p className="relative m-0 mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
          <Clock className="size-3.5" />O link vale por 1 hora
        </p>
        {newAccount.failed ? (
          <p className="relative m-0 mt-3 rounded-lg bg-white px-3 py-2 text-xs font-medium text-[#DC4E5A]">
            Não conseguimos enviar o e-mail agora. Toque em reenviar.
          </p>
        ) : null}
        <button
          type="button"
          disabled={newAccount.sending || newAccount.cooldown > 0}
          onClick={newAccount.onResend}
          className="relative mt-4 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-white text-sm font-semibold text-marketplace-900 disabled:cursor-not-allowed disabled:opacity-70">
          <MailCheck className="size-4" />
          {resendLabel(newAccount)}
        </button>
        <p className="relative m-0 mt-2.5 text-center text-[11px] text-white/75">
          Não achou? Confira a caixa de spam ou o lixo eletrônico.
        </p>
      </section>
    ) : null}
  </div>
)

export const DoneFooter = ({ onConsultation, newAccount }: { onConsultation: () => void; newAccount?: boolean }) => (
  <TrailCta onClick={onConsultation}>{newAccount ? 'Ir para o login' : 'Ver minha consulta'}</TrailCta>
)

export const ExitFooter = ({ showConsulta = false }: { showConsulta?: boolean }) => (
  <>
    {showConsulta ? (
      <TrailCta onClick={() => window.location.assign(CONSULTA_AVULSA_PATH)}>Agendar consulta avulsa</TrailCta>
    ) : null}
    <TrailGhost href={SUPPORT_WHATSAPP_URL}>Tire suas dúvidas com o nosso atendimento</TrailGhost>
  </>
)

export const OfferFooter = ({
  onAccept,
  onDecline,
  disabled
}: {
  onAccept: () => void
  onDecline: () => void
  disabled?: boolean
}) => (
  <>
    <TrailCta onClick={onAccept} disabled={disabled}>
      Quero seguir com o programa
    </TrailCta>
    <TrailGhost onClick={onDecline} disabled={disabled}>
      Agora não
    </TrailGhost>
  </>
)
